import { trpc } from '@/main'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { noop, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { useSessionUser } from '@/hooks/use-session'
import * as v from 'valibot'
import { Test } from '@/routes/_app/-components/applicants/test'
import { TestStepPending } from '@/routes/_app/-components/shared/flow-pending'
import { usePermissions } from '@/hooks/use-permissions'
import { flowViewTransition } from '@/lib/utils'

export const Route = createFileRoute('/_app/test/personality')({
  validateSearch: v.object({
    retake: v.optional(v.boolean(), false),
    resume: v.optional(v.boolean()),
    // Carried through onboarding so the final landing can return to the visitor's intended destination.
    redirect: v.optional(v.string()),
  }),
  // pending: rekey — `retake` only flips when a NEW test starts, a genuine screen change the skeleton belongs to.
  loaderDeps: ({ search }) => ({ retake: search.retake }),
  context: ({ deps }) => ({
    // No cache-lifetime overrides here — see test.competences.tsx for why `gcTime: 0` breaks the prefetch.
    testQuestionsQueryOptions: trpc.applicants.getQuestions.queryOptions({
      type: 'personality',
      newTest: deps.retake,
    }),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.testQuestionsQueryOptions).catch(noop)
  },
  component: RouteComponent,
  pendingComponent: TestStepPending,
})

function RouteComponent() {
  const navigate = useNavigate()
  const context = Route.useRouteContext()
  const { data: questionsResponse } = useSuspenseQuery(context.testQuestionsQueryOptions)
  const user = useSessionUser()
  const { retake, redirect } = Route.useSearch()
  // Logged-out → /login is enforced centrally by the `_app` layout guard (see route-access).
  // Self-registered applicants continue to the profile wizard (which honors `redirect` on completion);
  // company-invited employees skip it (their profile comes from the company) and go straight to their
  // report. Only the self-registered path carries `redirect` — employees arrive via invitation, never
  // the compare CTA.
  const isEmployee = user?.type === 'applicant' && user.isEmployee
  // EVERY employee lands on their own report — the answers they just gave are theirs, and an
  // administrator/authorized one has the same personal profile a collaborator does. The access level decides
  // what ELSE they can reach, never whether they get to see the profile they just retook the test for.
  const { isCompanyView } = usePermissions()
  const activeCompanyId = user?.type === 'applicant' ? user.activeCompanyId : null
  // …but /report has no meaning in the COMPANY view: the server refuses the self-applicant slot outright
  // (EMPLOYEE_VIEW_ONLY) and the layout guard bounces the route to their employee CARD. So an
  // admin/authorized employee who finished from that view returns to their EMPLOYEE interface first —
  // `setActiveEmployer` keeps the same employer and drops `interfaceMode` back to 'employee' — and the
  // report opens inside their own menu. Full document load, like every identity transition (see user-menu).
  const { mutateAsync: returnToEmployeeView } = useMutation(
    trpc.employees.setActiveEmployer.mutationOptions({
      onSuccess: () => navigate({ to: '/report', search: {}, reloadDocument: true }),
    }),
  )
  const goToOwnReport = () => {
    if (isCompanyView && activeCompanyId != null) return void returnToEmployeeView(activeCompanyId)
    return navigate({ to: '/report', viewTransition: flowViewTransition() })
  }
  return (
    <Test
      questionsResponse={questionsResponse}
      type="personality"
      nextStep={() =>
        isEmployee
          ? goToOwnReport()
          : navigate({ to: '/register/applicant', search: { redirect }, viewTransition: flowViewTransition() })
      }
      previousStep={() => navigate({ to: '/test/values', search: { redirect }, viewTransition: flowViewTransition() })}
      newTest={retake}
    />
  )
}
